/**
 * The editable cohort fields, shared by the create form and the cohort
 * detail page so the two cannot drift apart.
 *
 * Same conventions as clients/client-form-fields.tsx. Every field maps
 * 1:1 to a column the operator route accepts, and the help text carries
 * the WHY — these fields have consequences an operator cannot see from
 * the form (future enrollment defaults, invite timing, and classification
 * that applies only at enrollment/identity creation).
 */
import { Badge, Checkbox, FieldError, FieldHelp, FieldLabel, Input, Notice, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@speaking-coach/ui'
import { resolveSendDay } from './cohort-schema'
import { AccessWindowCell, todayJstIso } from '../common/access-window'
import {
  OPERATOR_FIELD_GROUP,
  OPERATOR_FIELDSET_LEGEND,
} from '../common/form-layout'
import { ProgramPicker } from '../common/programs'
import type { ProgramSummary } from '../common/programs'
import type { ProductTier } from './types'
import type { ReactNode } from 'react'
import { OperatorFaqTrigger } from '../operator-faq/operator-faq'

const PRODUCT_TIERS: ProductTier[] = ['essential', 'pro', 'premium', 'unlimited']

/** Fixed timestamp vs offset-from-start. Exactly the DB's CHECK, as a UI. */
export type SendMode = 'none' | 'fixed' | 'offset'

export function CohortNameField({
  value,
  onChange,
  error = null,
}: {
  value: string
  onChange: (v: string) => void
  error?: string | null
}): JSX.Element {
  return (
    <div>
      <FieldLabel
        htmlFor="cohort-name"
        label="Cohort name"
        help={
          <>
            Use the client&rsquo;s name for this group, such as “2026 new
            grads”. It only needs to be unique within this client.
          </>
        }
      />
      <Input
        id="cohort-name"
        type="text"
        required
        value={value}
        aria-invalid={error !== null || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {/* Without this, an empty name silently BLOCKED the submit: the
          schema refused it, handleSubmit never fired, and the message sat
          unrendered in formState — "nothing happens", the worst kind of
          validation. */}
      <FieldError message={error} />
    </div>
  )
}

export function CohortDescriptionField({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}): JSX.Element {
  return (
    <div>
      <FieldLabel
        htmlFor="cohort-description"
        label="Description (optional)"
        help="Operator notes only. Never shown to students or HR users."
      />
      <Textarea
        id="cohort-description"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

export function StartDateField({
  value,
  onChange,
  error = null,
}: {
  value: string
  onChange: (v: string) => void
  error?: string | null
}): JSX.Element {
  return (
    /* An AccessWindowCell, not a bare labeled input: this is the same
       "Program access window" concept the student pages render, and it
       looks the same everywhere on purpose (three variants had grown —
       see common/access-window.tsx). "Access starts" pairs with "Access
       ends" across every surface for the same reason. */
    <AccessWindowCell
      label="Access starts"
      htmlFor="cohort-start"
      help={
        <FieldHelp label="cohort access start">
          Required. New enrollments lock this start date and the cohort trophy
          goal. Existing enrollment periods never move when this default is
          edited.
        </FieldHelp>
      }
    >
      <Input
        id="cohort-start"
        type="date"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldError message={error} />
      {/* No "spread starts across weekdays" advice here: a cohort's new
          enrollments share a default window and trophy goal. Existing
          enrollment snapshots remain immutable. */}
    </AccessWindowCell>
  )
}

export function AccessEndsField({
  value,
  onChange,
  startDate,
  error = null,
}: {
  value: string
  onChange: (v: string) => void
  /** The cohort's start, when set — the earliest coherent end date. */
  startDate?: string
  error?: string | null
}): JSX.Element {
  // `min` stops the date picker offering an incoherent date; the inline
  // message covers typed input, which `min` does not block. The server
  // and a DB CHECK both refuse it regardless — this is so the operator
  // finds out while looking at the field rather than after a save.
  const endsBeforeStart =
    value !== '' && startDate !== undefined && startDate !== '' && value < startDate
  return (
    <AccessWindowCell
      label="Access ends"
      htmlFor="cohort-access-ends"
      help={
        <FieldHelp label="cohort access end">
          Required because practice sessions incur cost while access is
          active. Use a deliberately chosen far-future date for open-ended
          access; a later delivery should use a new cohort and enrollments.
        </FieldHelp>
      }
    >
      <Input
        id="cohort-access-ends"
        type="date"
        required
        value={value}
        min={startDate || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldError message={error} />
      {endsBeforeStart && (
        <p className="text-danger-text text-caption mt-1 font-normal">
          This is before the start date — the cohort would end before it
          begins.
        </p>
      )}
    </AccessWindowCell>
  )
}

export function ProductTierField({
  value,
  onChange,
  isEdit = false,
}: {
  value: ProductTier
  onChange: (v: ProductTier) => void
  /** True on the detail page — changes the help text to say it's forward-only. */
  isEdit?: boolean
}): JSX.Element {
  return (
    <div>
      <FieldLabel
        htmlFor="cohort-tier"
        label="Default tier"
        help={
          isEdit
            ? 'Applies only to enrollments created from now on. Existing enrollments keep their tier.'
            : 'Assigned to new enrollments in this cohort. Changing it later never re-tiers existing enrollments.'
        }
      />
      <Select
        value={value}
        onValueChange={(next) => onChange(next as ProductTier)}
      >
        <SelectTrigger id="cohort-tier">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRODUCT_TIERS.map((tier) => (
            <SelectItem key={tier} value={tier}>
              {tier}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function EnrollmentDefaultsFields({
  programId,
  currentProgram = null,
  trophyGoal,
  externalPolicyRef,
  onProgramChange,
  onTrophyGoalChange,
  onExternalPolicyRefChange,
  programIdError = null,
  trophyGoalError = null,
  isEdit = false,
}: {
  programId: string
  currentProgram?: ProgramSummary | null
  trophyGoal: string
  externalPolicyRef: string
  onProgramChange: (value: string) => void
  onTrophyGoalChange: (value: string) => void
  onExternalPolicyRefChange: (value: string) => void
  programIdError?: string | null
  trophyGoalError?: string | null
  isEdit?: boolean
}): JSX.Element {
  return (
    <fieldset className={OPERATOR_FIELD_GROUP}>
      <legend className={OPERATOR_FIELDSET_LEGEND}>
        <span className="flex items-center gap-1 font-medium">
          Future enrollment defaults
          <FieldHelp label="future enrollment defaults">
            Reviewed and snapshotted when each enrollment is created.
            {isEdit
              ? ' Existing enrollments keep the terms they already locked.'
              : ''}{' '}
            Company enrollments always start a new diagnostic, curriculum
            attempt, and trophy progress.
          </FieldHelp>
        </span>
      </legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <FieldLabel
            htmlFor="cohort-program"
            label="Program"
            size="compact"
            help={
              <>
              The program for this cohort. Each
              student's diagnostic resolves the exact program level.
              Editing this default never rewrites an existing enrollment.
              </>
            }
          />
          <ProgramPicker
            requireReady
            id="cohort-program"
            value={programId}
            currentProgram={currentProgram}
            invalid={programIdError !== null}
            onValueChange={onProgramChange}
          />
          <FieldError message={programIdError} />
        </div>
        <div>
          <FieldLabel
            htmlFor="cohort-trophy-goal"
            label="Trophy goal"
            size="compact"
            help={
              <>
              The completion gate agreed for each enrollment in this cohort.
              It may support reimbursement or mandatory training, but the
              funding arrangement itself is not modeled.
              </>
            }
          />
          <Input
            id="cohort-trophy-goal"
            type="number"
            min={1}
            step={1}
            value={trophyGoal}
            aria-invalid={trophyGoalError !== null || undefined}
            onChange={(event) => onTrophyGoalChange(event.target.value)}
          />
          <FieldError message={trophyGoalError} />
        </div>
      </div>
      <div>
        <FieldLabel
          htmlFor="cohort-external-policy-ref"
          label="External policy reference (optional)"
          size="compact"
          help={
            <>
            An opaque CRM, contract, purchase order, or policy identifier.
            Cooori stores it for traceability but never interprets it or uses
            it to decide access, payment, or completion.
            </>
          }
        />
        <Input
          id="cohort-external-policy-ref"
          value={externalPolicyRef}
          onChange={(event) => onExternalPolicyRefChange(event.target.value)}
        />
      </div>
    </fieldset>
  )
}

export function SendScheduleFields({
  mode,
  fixedAt,
  offsetDays,
  hasStartDate,
  startDate,
  accessEndsOn,
  onModeChange,
  onFixedChange,
  onOffsetChange,
  error = null,
}: {
  mode: SendMode
  /** `datetime-local` value, or ''. */
  fixedAt: string
  offsetDays: string
  /** An offset needs something to be relative to — the server enforces this too. */
  hasStartDate: boolean
  /** ISO date, for resolving what an offset actually lands on. */
  startDate?: string
  /** ISO date. A send after this arrives as a dead link. */
  accessEndsOn?: string
  onModeChange: (m: SendMode) => void
  onFixedChange: (v: string) => void
  onOffsetChange: (v: string) => void
  /** Schema message for whichever send field is at fault. */
  error?: string | null
}): JSX.Element {
  // A send BEFORE the start is normal (-7) and a send AFTER the start is
  // legitimate (a straggler joining an in-flight cohort). A send after
  // access has ENDED is never coherent: the invite is dead on arrival.
  const resolvedSendDay = resolveSendDay(mode, fixedAt, offsetDays, startDate)
  const sendsAfterEnd =
    resolvedSendDay !== null &&
    accessEndsOn !== undefined &&
    accessEndsOn !== '' &&
    resolvedSendDay > accessEndsOn
  // "Start tomorrow, minus seven days" quietly resolves to last week —
  // which in practice means IMMEDIATE sending (new students at creation,
  // unsent members on the next hourly run). Legitimate, but the operator
  // should not need to do the arithmetic to notice.
  //
  // The comparison is against the send INSTANT, not the calendar day: a
  // relative date resolves to 09:00 Japan time, so "today" before 09:00
  // is a future send later this morning, not an immediate one — the two
  // get different sentences. A fixed send compares against its own
  // chosen time (browser-local, exactly what sendScheduleToBody stores).
  const resolvedInstantMs =
    resolvedSendDay === null
      ? null
      : mode === 'fixed' && fixedAt !== ''
        ? Date.parse(fixedAt)
        : Date.parse(`${resolvedSendDay}T09:00:00+09:00`)
  const sendsImmediately =
    !sendsAfterEnd && resolvedInstantMs !== null && resolvedInstantMs <= Date.now()
  const sendsLaterToday =
    !sendsAfterEnd &&
    !sendsImmediately &&
    resolvedSendDay !== null &&
    resolvedSendDay <= todayJstIso()
  // The whole group waits for the start date (which is required anyway):
  // even a fixed send is validated against the window, and a disabled
  // relative radio in an otherwise-live group was limbo, not guidance.
  // Same progressive disclosure as the batch form's steps.
  if (!hasStartDate) {
    return (
      <fieldset className={OPERATOR_FIELD_GROUP}>
        <legend className={OPERATOR_FIELDSET_LEGEND}>Invite send</legend>
        <p className="text-text-secondary text-caption">
          Set the start date first — the send schedule is defined against
          it, and defaults to the day before the start.
        </p>
      </fieldset>
    )
  }
  return (
    <fieldset className={OPERATOR_FIELD_GROUP}>
      <legend className={OPERATOR_FIELDSET_LEGEND}>
        <span className="flex items-center gap-2">
          Invite send
          <OperatorFaqTrigger
            topicId="cohort-enrollment-defaults"
            label="Open guide for cohort invite timing"
            iconOnly
          />
        </span>
      </legend>
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 text-body">
          <input
            type="radio"
            name="cohort-send-mode"
            value="none"
            checked={mode === 'none'}
            onChange={() => onModeChange('none')}
          />
          No schedule — each student is invited when created
        </label>
        {/* Enrollment creation always owns delivery creation. With no cohort
            schedule it creates a due-now ledger row; changing this default
            later never mutates deliveries already queued. */}
        {mode === 'none' && (
          <p className="text-text-secondary text-caption ml-6 -mt-1">
            New enrollments are queued immediately. Existing invitation
            deliveries keep their current schedule.
          </p>
        )}

        <label className="flex items-center gap-2 text-body">
          <input
            type="radio"
            name="cohort-send-mode"
            value="offset"
            checked={mode === 'offset'}
            disabled={!hasStartDate}
            onChange={() => onModeChange('offset')}
          />
          Relative to the start date
        </label>
        {mode === 'offset' && (
          <div className="pl-6">
            <FieldLabel
              htmlFor="cohort-send-offset"
              label="Days from the start (negative = before)"
              size="compact"
              helpLabel="relative invite timing"
              help={
                <>
                −7 sends at 09:00 JST one week before the program. Avoid very
                early sends because activation links expire.
                </>
              }
            />
            <Input
              id="cohort-send-offset"
              type="number"
              min={-365}
              // 0 = the start day. Positive offsets are refused (schema +
              // server): a relative send leads the start; sending after it
              // is the fixed date's job.
              max={0}
              value={offsetDays}
              onChange={(e) => onOffsetChange(e.target.value)}
              className="w-32"
            />
          </div>
        )}
        {!hasStartDate && (
          <p className="text-text-secondary text-caption pl-6">
            Set a start date to use a relative send date.
          </p>
        )}

        <label className="flex items-center gap-2 text-body">
          <input
            type="radio"
            name="cohort-send-mode"
            value="fixed"
            checked={mode === 'fixed'}
            onChange={() => onModeChange('fixed')}
          />
          On a fixed date and time
        </label>
        {/* Kept as the LIVE hint while typing; the schema states the same rule
            on submit and blocks the save. Two renderings of one rule is the
            duplication the zod migration is supposed to remove, so this one
            says only WHAT and leaves the full sentence (with both dates) to the
            schema's message below. */}
        {sendsAfterEnd && (
          <p className="text-danger-text text-caption">
            This send lands after access ends.
          </p>
        )}
        {sendsImmediately && (
          <Notice variant="warning" textSize="caption">
            This resolves to a time already past, so each new enrollment is
            queued immediately. Existing invitation deliveries do not move.
          </Notice>
        )}
        {sendsLaterToday && (
          <Notice variant="warning" textSize="caption">
            This resolves to TODAY — invitations go out{' '}
            {mode === 'fixed' ? 'at the chosen time today' : 'at 09:00 Japan time today'}.
          </Notice>
        )}
        {mode === 'fixed' && (
          <div className="pl-6">
            <FieldLabel
              htmlFor="cohort-send-at"
              label="Send at"
              size="compact"
              helpLabel="fixed invite time"
              help={
                <>
                A fixed time does not follow the cohort. Update it explicitly if
                the start date changes.
                </>
              }
            />
            <Input
              id="cohort-send-at"
              type="datetime-local"
              value={fixedAt}
              max={accessEndsOn ? `${accessEndsOn}T23:59` : undefined}
              onChange={(e) => onFixedChange(e.target.value)}
            />
          </div>
        )}
      </div>
      {/* One slot for whichever send field the schema faulted — the mode, the
          fixed timestamp and the offset are three inputs expressing one
          setting, so an error about any of them belongs to the group. */}
      <FieldError message={error} />
    </fieldset>
  )
}

export function CohortInternalField({
  value,
  onChange,
  clientIsInternal,
}: {
  /** Three-state: null = inherit the client's setting. */
  value: boolean | null
  onChange: (v: boolean | null) => void
  /**
   * What the client actually says, so "Same as the client" is not a blind
   * choice. `undefined` while the client has not loaded — and the label then
   * stays generic rather than filling in `customer`, because a confidently
   * wrong resolution is worse than an unresolved one: an operator who reads
   * "same as the client — customer" and accepts it has been told something
   * false, whereas an unlabeled option merely tells them nothing.
   */
  clientIsInternal?: boolean
}): JSX.Element {
  const inheritLabel =
    clientIsInternal === undefined
      ? 'Same as the client (default)'
      : `Same as the client — ${clientIsInternal ? 'internal' : 'customer'} (default)`

  // Internal is sticky downward (ADR-0052 D8c, amended 2026-08-06): every
  // cohort in an internal client is internal, so there is nothing left to
  // choose. Read-only rather than a control the server would refuse — the
  // admin rule is never to offer an action already known to be rejected.
  if (clientIsInternal === true) {
    return (
      <div>
        <FieldLabel
          label="Customer reporting"
          size="compact"
          helpLabel="cohort classification"
          help={
            <>
            Internal activity is excluded from customer analytics. It does not
            change the student experience, email delivery, or access.
            </>
          }
        />
        <p className="text-body">Internal — inherited from the client.</p>
      </div>
    )
  }

  return (
    <div>
      <FieldLabel
        htmlFor="cohort-internal"
        label="Customer reporting"
        size="compact"
        help={
          <>
            Internal excludes this group from customer analytics without
            changing access, emails, or the student experience. The choice
            applies to new members; existing students keep their classification.
          </>
        }
      />
      <Select
        value={value === null ? 'inherit' : value ? 'internal' : 'customer'}
        onValueChange={(next) =>
          onChange(
            next === 'inherit'
              ? null
              : next === 'internal',
          )
        }
      >
        <SelectTrigger id="cohort-internal">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {/* The resolved value in the OPTION label, not only in the help text
              below it. This is where the choice is made, and a selected trigger
              collapses to whatever this string says — so an operator who picks
              inherit and moves on still sees which way it resolved. */}
          {/* Two options, not three. The third used to be "Customer — even if
              the client is internal", which is the state D8c now excludes
              (amended 2026-08-06) — and inside a CUSTOMER client it resolved
              identically to inherit, so it never said anything the default did
              not. An option whose only distinct meaning is forbidden is not an
              option. */}
          <SelectItem value="inherit">{inheritLabel}</SelectItem>
          <SelectItem value="internal">Internal — not a customer</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

export function CohortTrialField({
  value,
  onChange,
  locked = false,
}: {
  value: boolean
  onChange: (value: boolean) => void
  locked?: boolean
}): JSX.Element {
  return (
    <div>
      <FieldLabel
        label="Trial usage"
        size="compact"
        helpLabel="trial cohort"
        help={
          <>
            Marks this cohort and each enrollment created from it as a trial so
            analytics can exclude evaluation activity. It does not change
            access, invitations, enrollment dates, or the student experience.
          </>
        }
      />
      <label className="flex items-center gap-2 text-body">
        <Checkbox
          checked={value}
          disabled={locked}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
        Trial cohort
      </label>
      {locked && (
        <p className="mt-1 text-caption text-text-secondary">
          Locked because this cohort already has enrollment history.
        </p>
      )}
    </div>
  )
}

/** Keep the two reporting dimensions together so neither looks behavioral. */
export function CohortClassificationFields({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return (
    <fieldset className={OPERATOR_FIELD_GROUP}>
      <legend className={OPERATOR_FIELDSET_LEGEND}>Classification</legend>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  )
}

/**
 * Why the save button is disabled.
 *
 * A disabled button with no explanation is worse than a rejected save, so
 * the reasons are always rendered beside it. `role="alert"` because it
 * appears as a consequence of what the operator just typed.
 */
export function FormBlockers({ errors }: { errors: string[] }): JSX.Element | null {
  if (errors.length === 0) return null
  return (
    <Notice variant="danger">
      <p className="font-medium mb-1">
        {errors.length === 1
          ? 'One thing to fix before saving:'
          : `${errors.length} things to fix before saving:`}
      </p>
      <ul className="list-disc pl-5 space-y-0.5">
        {errors.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </Notice>
  )
}

/**
 * Shared badge, used on cohort rows, the cohort detail page and the student
 * students list.
 *
 * Now a thin wrapper over the shared `Badge` (`@speaking-coach/ui`), which is where the styling
 * lives. Kept as a named component rather than inlining `<Badge>internal</Badge>`
 * at four call sites, because the WORD is part of the contract: "internal"
 * means excluded from customer analytics and never an access rule, and four
 * copies of that string is four chances for one of them to become "test" or
 * "cooori" and quietly mean something else.
 */
export function InternalBadge(): JSX.Element {
  return <Badge variant="neutral">internal</Badge>
}
